import { QuoteCurrencyList } from '@flipster/utils/constants/currency';
import { valueof } from '@flipster/utils/typescript';

// TODO: [sh] 이거 atom/v2/market에 중복
export interface MarketPswap {
  markPrice: string;
  midPrice: string;
  priceChangePct24h: string;
  turnover24h: string;
  fundingRate: string;
  fundingTime: string;
  fundingTimeBlocking?: FundingTimeBlocking;
}

export type FundingTimeBlockingNone = { None: { interval: number } };
export type FundingTimeBlockingLikely = { Likely: { interval: number } };
export type FundingTimeBlockingScheduled = { Scheduled: { from: string; until: string } };
export type FundingTimeBlocking = FundingTimeBlockingNone | FundingTimeBlockingLikely | FundingTimeBlockingScheduled;

export interface MarketPswapDetail {
  midPrice: string;
  bidPrice: string;
  askPrice: string;
}

export interface AlphaPublicSnapshot {
  t: {
    'market/pswaps': {
      s: Record<string, MarketPswap>;
    };
    'market/pswap-details': {
      s: Record<string, MarketPswapDetail>;
    };
  };
}

export const FundingTypeEnum = {
  Invalid: 'FUNDING_TYPE_INVALID',
  Realtime: 'FUNDING_TYPE_1MIN_REALTIME', // Deribit way
  DelayedHour8: 'FUNDING_TYPE_8HOURS_DELAYED', // Bitmex, OKEX, HBDM way
  ImmediateHour8: 'FUNDING_TYPE_8HOURS_IMMEDIATE', // Binance futures way
  ImmediateHour4: 'FUNDING_TYPE_4HOURS_IMMEDIATE', // Something between Binance and FTX. Mainly for emergency
  ImmediateHour2: 'FUNDING_TYPE_2HOURS_IMMEDIATE', // Something between Binance and FTX. Mainly for emergency
  ImmediateHour1: 'FUNDING_TYPE_1HOUR_IMMEDIATE', // FTX way
} as const;

type FundingTypeType = valueof<typeof FundingTypeEnum>;

export type InstrumentProductType = valueof<typeof InstrumentProductTypeEnum>;
export const InstrumentProductTypeEnum = {
  Invalid: 'PRODUCT_TYPE_INVALID',
  Pswap: 'PRODUCT_TYPE_PERPETUAL_SWAP',
  Futures: 'PRODUCT_TYPE_FUTURES',
  Option: 'PRODUCT_TYPE_OPTION',
  Spot: 'PRODUCT_TYPE_SPOT',
  Index: 'PRODUCT_TYPE_INDEX',
} as const;

export interface Instrument {
  symbol: string;
  keywords?: string[];
  icon: string;
  crossMarginSupported: boolean;
  displayName: string;
  displayShortName: string;
  productType: valueof<typeof InstrumentProductTypeEnum>;
  tickSize: string;
  quoteCurrency: (typeof QuoteCurrencyList)[number];
  positionUnit: string;
  notionalMinOrderAmount: string;
  notionalMaxOrderAmount: string;
  notionalMaxPosition: string;
  defaultMaxSlippage: string;
  unitOrderQty: string;
  initMarginRate: string;
  maintMarginRate: string;
  baseCurrency: string;
}

export interface InstrumentApi extends Instrument {
  pricePrecision: number;
  baseInterestRate: string;
  fundingType: FundingTypeType;
  fundingRateClipOverride?: string;
  longPositionReduceOnlyTriggerOiInUsdt: string;
  shortPositionReduceOnlyTriggerOiInUsdt: string;
  isListed: boolean;
  maxBuyAmountDaily: string;
  maxSellAmountDaily: string;
}

export const CurrencyTypeEnum = {
  invalid: 'CURRENCY_TYPE_INVALID',
  fiat: 'CURRENCY_TYPE_FIAT',
  crypto: 'CURRENCY_TYPE_CRYPTO',
  internal: 'CURRENCY_TYPE_INTERNAL',
} as const;

export const PriceTypeEnum = {
  invalid: 'CURRENCY_PRICE_TYPE_INVALID',
  usdLike: 'CURRENCY_PRICE_TYPE_USD_LIKE',
  symbol: 'CURRENCY_PRICE_TYPE_SYMBOL',
  usdPegged: 'CURRENCY_PRICE_TYPE_USD_PEGGED',
  usdtLike: 'CURRENCY_PRICE_TYPE_USDT_LIKE',
  usdtPegged: 'CURRENCY_PRICE_TYPE_USDT_PEGGED',
} as const;

export const NetworkTypeEnum = {
  invalid: 'NETWORK_TYPE_INVALID',
  mainnet: 'NETWORK_TYPE_MAINNET',
  erc20: 'NETWORK_TYPE_ERC20',
} as const;

export interface BlockchainInfo {
  blockchainName: string;
  blockchainAsset: string;
  networkName: string;
  icon: string;
  txidExplorerLink: string;
  addressExplorerLink: string;
  contractExplorerLink: string;
  depositAvailable: boolean;
  withdrawalAvailable: boolean;
  withdrawalFee: string;
  minWithdrawalAmount: string;
  withdrawalPrecision: number;
  minConfirmations: number;
  addressParams: string[]; // destinationTag, memo
}

export interface Currency {
  currency: string;
  keywords: string[];
  englishName: string;
  description: string;
  currencyType: valueof<typeof CurrencyTypeEnum>;
  icon: string;
  displayOrder: number;
  conversionAvailable: boolean;
  conversionPrecision: number;
  conversionUnit: string;
  collateralAvailable: boolean;
  collateralRate: string;
  fullDisplayPrecision: number;
  usdRateSymbol: string;
  buyUsdRateSymbol: string;
  sellUsdRateSymbol: string;
  usdtRateSymbol: string;
  priceType: valueof<typeof PriceTypeEnum>;
  networkType: valueof<typeof NetworkTypeEnum>;
  blockchainInfo: BlockchainInfo[];
  withdrawalBadgeString: string | null;
}

export interface SpecSnapshot {
  t: {
    instruments: {
      s: Record<string, InstrumentApi>;
    };
    currencies: {
      s: Record<string, Currency>;
    };
  };
}
